"use client"

import { useState } from "react"
import { useTranslations } from "next-intl"
import { isOfferEnabledFormat, type ListingFormat } from "@kraahl/contract"
import { CommerceIcons } from "@/lib/icons/commerce-icons"
import { Button } from "@/components/ui/button"
import AuthModal from "@/components/ui/auth-modal"
import { OfferModal } from "./offer-modal"
import { cn } from "@/lib/utils"

type MakeOfferButtonVariant = "default" | "feed" | "detail" | "ghost" | "foil"

interface MakeOfferButtonProps {
  listingId: string
  currentUserId: string | null
  sellerId: string
  listingFormat: string
  // Phase 3 (2026-06-29): `acceptsOffers` prop dropped — derive from
  // `listingFormat` via `isOfferEnabledFormat()` (the listings column
  // collapsed; format is the SoT).
  listingPrice: number | null
  currency: string | null
  listingTitle?: string
  listingImageUrl?: string | null
  listingCondition?: string | null
  listingGrade?: string | number | null
  sellerUsername?: string | null
  variant?: MakeOfferButtonVariant
  className?: string
  fullWidth?: boolean
  onSuccess?: (offerId: string) => void
}

export function MakeOfferButton({
  listingId,
  currentUserId,
  sellerId,
  listingFormat,
  listingPrice,
  currency,
  listingTitle,
  listingImageUrl,
  listingCondition,
  listingGrade,
  sellerUsername,
  variant = "default",
  className,
  fullWidth,
  onSuccess,
}: MakeOfferButtonProps) {
  const t = useTranslations()
  const [open, setOpen] = useState(false)
  const [authOpen, setAuthOpen] = useState(false)
  const OfferIcon = CommerceIcons.offer

  const isOwner = currentUserId != null && currentUserId === sellerId
  // Phase 3 (2026-06-29): "buyers can offer" is purely a function of the
  // format discriminator. Treat unknown values as non-offer-enabled
  // (defensive narrow before the typed predicate).
  const accepts = isKnownFormat(listingFormat) && isOfferEnabledFormat(listingFormat)

  if (isOwner || !accepts) return null

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!currentUserId) {
      setAuthOpen(true)
      return
    }
    setOpen(true)
  }

  const buttonClasses = cn(
    {
      "w-full": fullWidth,
    },
    variant === "feed" &&
      "bg-white/15 hover:bg-white/25 text-white border-0 backdrop-blur-md font-medium",
    variant === "ghost" && "border border-foreground/15 bg-transparent text-foreground hover:bg-muted/60",
    className,
  )

  const buttonVariant: "secondary" | "default" | "outline" | "foil" =
    variant === "feed"
      ? "secondary"
      : variant === "ghost"
        ? "outline"
        : variant === "foil"
          ? "foil"
          : "default"

  return (
    <>
      <Button onClick={handleClick} className={buttonClasses} variant={buttonVariant}>
        <OfferIcon className="size-3.5 shrink-0" />
        <span className="min-w-0 truncate">{t("offers.makeOfferCta", { default: "Offer" })}</span>
      </Button>

      <OfferModal
        open={open}
        onOpenChange={setOpen}
        mode={{ kind: "create", listingId, listingPrice, currency }}
        listingPreview={
          listingTitle
            ? {
                title: listingTitle,
                imageUrl: listingImageUrl ?? null,
                condition: listingCondition,
                grade: listingGrade,
              }
            : undefined
        }
        sellerUsername={sellerUsername}
        onSuccess={onSuccess}
      />

      <AuthModal
        isOpen={authOpen}
        onCloseAction={() => setAuthOpen(false)}
        message={t("offers.authPrompt", {
          default: "Please log in or register to make an offer.",
        })}
      />
    </>
  )
}

// Phase 3 (2026-06-29) helper — narrows an arbitrary listing_format string
// into the canonical `ListingFormat` literal union before calling the
// `isOfferEnabledFormat` predicate. Defensive: returns false on unknown
// strings so a stale value never re-opens the offer path.
function isKnownFormat(format: string): format is ListingFormat {
  return (
    format === "fixed_price" ||
    format === "fixed_price_with_offers" ||
    format === "offers_only" ||
    format === "auction"
  )
}
